import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';

const POLL_MS = 60_000;

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 5H6.75A1.75 1.75 0 0 0 5 6.75v12.5C5 20.22 5.78 21 6.75 21h10.5c.97 0 1.75-.78 1.75-1.75V6.75C19 5.78 18.22 5 17.25 5H15" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="m8 12 1.5 1.5L12 10.5M14 12h2M8 17l1.5 1.5L12 15.5M14 17h2" />
    </svg>
  );
}

function shortDate(value) {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function ServiceDeliveryBell({ tone = 'dark' }) {
  const navigate = useNavigate();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ tasks: [], active_count: 0, start_window_count: 0, overdue_count: 0, badge_count: 0 });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get('/api/notifications/service-delivery'));
      setError(null);
    } catch (err) {
      setError(err?.body?.error || err?.message || 'Could not load service delivery tasks');
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [load]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => { if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const iconTone = tone === 'light' ? 'text-hunter-deep hover:text-mid-green' : 'text-silver-a hover:text-white';

  return (
    <div ref={wrapRef} className="relative">
      <button type="button" onClick={() => setOpen((value) => !value)} className={`relative inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors ${iconTone} focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green/50`} aria-label={`${data.badge_count || 0} service delivery tasks`} aria-haspopup="true" aria-expanded={open} title="Service delivery">
        <ClipboardIcon />
        {data.badge_count > 0 && <span className={`absolute right-0 top-0 h-[1.15rem] min-w-[1.15rem] rounded-full px-1 text-center text-[0.65rem] font-bold leading-[1.15rem] text-white shadow ${data.overdue_count > 0 ? 'bg-red-600' : 'bg-mid-green'}`}>{data.badge_count > 99 ? '99+' : data.badge_count}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[32rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-gray-200 bg-white text-silver-text shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <div className="font-display text-base font-black text-hunter">Service delivery</div>
              <div className="text-xs text-silver-mute">{data.start_window_count} in their start window</div>
            </div>
            <div className={data.overdue_count ? 'text-sm font-bold text-red-700' : 'text-sm text-silver-mute'}>{data.overdue_count} overdue</div>
          </div>
          <div className="max-h-[min(70vh,42rem)] overflow-y-auto">
            {error && <div className="px-4 py-4 text-sm text-red-700">{error}</div>}
            {!error && !data.tasks?.length && <div className="px-4 py-8 text-center text-sm text-silver-mute">No service tasks are in their start window.</div>}
            {(data.tasks || []).map((task) => (
              <button
                key={task.id}
                type="button"
                onClick={() => { setOpen(false); navigate(task.link); }}
                className={`block w-full border-b border-gray-100 px-4 py-3.5 text-left last:border-0 hover:bg-silver-a/60 ${task.overdue ? 'bg-red-50/70' : ''}`}
              >
                <span className="block text-base font-semibold text-silver-text">{task.title}</span>
                <span className="mt-0.5 block text-sm text-silver-mute">{task.recruit_name}</span>
                <span className={`mt-1 block text-xs font-bold uppercase tracking-wide ${task.overdue ? 'text-red-700' : 'text-mid-green'}`}>
                  {task.overdue ? `Overdue · due ${shortDate(task.deadline_date)}` : `Started ${shortDate(task.start_date)} · due ${shortDate(task.deadline_date)}`}
                </span>
              </button>
            ))}
          </div>
          <button type="button" onClick={() => { setOpen(false); navigate('/call-sheet'); }} className="block w-full border-t border-gray-100 px-4 py-3 text-center text-sm font-bold text-hunter hover:bg-silver-a/60">
            View the Call Sheet
          </button>
        </div>
      )}
    </div>
  );
}
